import React, { useState, useRef, useEffect } from 'react';
import config from '../../config/config';

const DocSharkSearchGuide = ({ isOpen, onClose }) => {
  const overlayRef = useRef(null);
  const contentRef = useRef(null);

  // Handle clicking outside the popup
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the overlay ref exists and the click is outside the content
      if (
        overlayRef.current &&
        contentRef.current &&
        !contentRef.current.contains(event.target)
      ) {
        onClose();
      }
    };

    // Add event listener when popup is open
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);

      // Prevent background scrolling when popup is open
      document.body.style.overflow = 'hidden';
    }

    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // If not open, return null
  if (!isOpen) return null;

  return (
    <div
      ref={overlayRef}
      className="docshark-search-guide-overlay"
    >
      <div
        ref={contentRef}
        className="docshark-search-guide"
      >
        <div className="search-guide-header">
          <h2>DocShark🦈 Search Guide</h2>
          <button
            className="close-button"
            aria-label="Close"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div class="search-guide-content">
          <p>
            Welcome to the DocShark Search Guide!
            This guide is your cheatsheet to effectively use DocShark's search features, helping to find the information you need.
            It provides examples and explanations for basic and advanced search techniques.
          </p>

          <div class="menu-options">
            <h2>Menu options</h2>
            <div class="search-section">

              <h3><strong>Choose Scope:</strong></h3>
              <ul>
                <li><strong>All EMRD Medicines:</strong> Search across all medicines within the European Medicines Regulatory Database (EMRD).</li>
                <li><strong>Current EMRD Selection:</strong> Limit your search to the medicines currently selected on the <i className="bx bx-data"></i>{' '}
                  <a href={config.routes.main.data} className="med-link">data page</a>. This allows for focused searches on specific sets of medicines.</li>
              </ul>
            </div>
            <div class="search-section">
              <h3><strong>Filter by Document Type:</strong></h3>
              <p><strong>Tip:</strong><em> By default, DocShark searches through all available document types. For more specific results, select one or more document types above.</em></p>
              <ul>
                <li><strong>EPAR Documents:</strong> Includes EMA marketing documents: Public Assessment Report, Scientific Discussion, and procedural steps taken during the initial authorisation process.</li>
                <li><strong>Decision:</strong> Search within the official marketing authorisation decisions related to the medicines.</li>
                <li><strong>SmpC:</strong> Focus your search on marketing authorisation annexes, which often contain detailed supplementary information.</li>
              </ul>
            </div>
            <div class="search-section">
              <h3><strong>Include Images <i class='bx bx-image-alt'></i>:</strong></h3>
              <p>
                Enable this to <strong>search for text within images</strong> in documents.
                DocShark uses AI to read text directly from images, including images of tables, and will show you the exact page number where your search terms are found.
                <i> Please note that the accuracy of image content search can vary based on image quality and orientation.</i>
              </p>
            </div>
            <div class="search-section">
              <h3>Exporting Search Results</h3>
              <p>
                DocShark allows you to export your search results for further analysis.
              </p>
              <ul>
                <li>
                  <strong>Selection:</strong> Use the checkboxes next to each medicine hit to select the medicines of which you want to export search results.
                </li>
                <li>
                  <strong>Formats:</strong> You can export in either Comma Separated Values (.csv) or Excel (.xlsx) format.
                </li>
                <li>
                  <strong>Product Data:</strong>  You have the option to include additional EMRD product data in your export.  If selected, this will add columns with information such as the medicine's ATC codes, marketing authorisation holder, etc.
                </li>
              </ul>
            </div>
          </div>
          <h2>DocShark🦈 Cheatsheet</h2>
          <div class="note">
            <p><strong>Note:</strong> DocShark search is not case sensitive. This means searching for "ibuprofen" will also find "Ibuprofen" and "IBUPROFEN".</p>
          </div>

          <div class="search-section">
            <h3>Basic Search Features</h3>
            <div class="search-example">
              <strong>Single Word Search</strong>
              <code>ibuprofen</code>
              <div class="explanation">Finds documents containing "ibuprofen"</div>
            </div>
            <div class="search-example">
              <strong>Multi Word Search</strong>
              <div class="explanation">By default a space between words will be evaluated as AND:</div>
              <code>ibuprofen gel</code>
              <div class="explanation">Finds documents containing both "ibuprofen" AND "gel"</div>
            </div>
            <div class="search-example">
              <strong>Exact Phrase Search with "</strong>
              <div class="explanation">Where <code>50mg tablet</code> finds documents containing both <code>50mg</code> and <code>tablet</code>. An exact phrase can be found by surrounding a text with quotes ("):</div>
              <code>"50mg tablet"</code>
              <div class="explanation">Finds the exact phrase "50mg tablet" in documents</div>
            </div>
            <div class="search-example">
              <strong>Alternative Search with OR</strong>
              <code>"ibuprofen gel" OR "pain relief"</code>
              <div class="explanation">Finds documents containing either "ibuprofen gel" OR "pain relief"</div>
            </div>
          </div>

          <div class="search-section">
            <h3>Advanced Search Features</h3>
            <div class="search-example">
              <strong>Exclusion Search</strong>
              <code>adult NOT child</code>
              <div class="explanation">Finds documents containing "adult", without containing "child"</div>
            </div>
            <div class="search-example">
              <strong>Proximity Search</strong>
              <code>"term1 term2"~5</code>
              <div class="explanation">Finds documents where "term2" is within 5 words after "term1"</div>
            </div>
            <div class="search-example">
              <strong>Grouping with Parentheses ( )</strong>
              <div class="explanation">For larger searches, it is important to be mindfull of brackets and the order of operators. For example, without brackets:</div>
              <code>migraine OR headache AND treatment</code>
              <div class="explanation">is evaluated differently then:</div>
              <code>headache OR migraine AND treatment</code>
              <div class="explanation">When you don’t use parentheses, operators like <code>AND</code> and <code>OR</code> are evaluated in a default order. With <code>AND</code> taking precedence over <code>OR</code>. This can lead to unexpected search results. In the example above:</div>
              <code>migraine OR headache AND treatment</code>
              <div class="explanation">is typically interpreted as:</div>
              <code>migraine OR (headache AND treatment)</code>
              <div class="explanation">This means the search will return results that either mention migraine alone, or mention both headache and treatment</div>
              <br></br>
              <div class="explanation">To change the logic of the search, you can use parentheses to group your terms more precisely:</div>
              <code>(migraine OR headache) AND treatment</code>
              <div class="explanation">This ensures that the search returns only results that mention treatment along with either migraine or headache.</div>
            </div>
          </div>

          <div class="search-section">
            <h3>Wildcard Search Features</h3>
            <div class="search-example">
              <strong>Multi-Character Wildcard (*)</strong>
              <code>gel*</code>
              <div class="explanation">Matches any number of characters (gel, gels, gelled, gelcaps, etc.)</div>
            </div>
            <div class="search-example">
              <strong>Single-Character Wildcard (?)</strong>
              <code>ibuprof?n</code>
              <div class="explanation">Matches exactly one character (ibuprofen, ibuprofin)</div>
            </div>
            <div class="search-example">
              <strong>Combined Wildcards</strong>
              <code>cort*id?</code>
              <div class="explanation">Finds terms like "corticoid," "corticoids,", "corticosteroids" or "cortasid."</div>
            </div>
          </div>

          <div class="search-section">
            <h3>Search Tips</h3>
            <div class="search-example">
              <strong>Combine Techniques</strong>
              <code>"adverse events" AND adult* NOT (pediatric OR child*)</code>
              <div class="explanation">Combines exact phrase, wildcards, and exclusions for more specific results. Stay mindfull of brackets and the order of operators</div>
            </div>
          </div>

          <div class="note">
            <p><strong>Tip:</strong> For the most comprehensive results, try different search techniques and combinations. Start with broader terms and then refine with advanced operators as needed.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocSharkSearchGuide;